import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  renderCurrencyIcon,
  renderCurrencyIconColored,
} from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { StatusPill } from "../../ContractorProjectDetails/components/Changes";
import { formatDate, isoDay, parseDeliverables } from "./helpers";

export default function PreviewTab({ cr, preview, loading }) {
  if (loading || !preview) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 6,
          color: "#9CA3AF",
        }}
      >
        <CircularProgress size={26} />
        <Box sx={{ fontFamily: "Inter Tight", fontSize: 13 }}>
          Loading preview…
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            Review Change Request
          </Typography>
          {cr?.status && <StatusPill status={cr.status} />}
        </Box>
      </Box>

      {/* Summary card — 2 columns: Original (with +/- price change
          stacked underneath) and Proposed. */}
      <Box
        sx={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          <Box>
            <SummaryStat
              label="Original Project Cost"
              value={preview.originalProposalCost}
            />
            <PriceChangeDelta value={preview.priceChange} />
          </Box>
          <SummaryStat
            label="Proposed Project Cost"
            value={preview.proposedProposalCost}
            highlight
          />
        </Box>
        {cr?.notes && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: "#9CA3AF",
                fontFamily: "Inter Tight",
                mb: 0.5,
              }}
            >
              Notes
            </Typography>
            <Box
              sx={{
                fontSize: 14,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                "& p": { margin: 0 },
                "& ul, & ol": { margin: 0, paddingLeft: "20px" },
                "& strong, & b": { fontWeight: 600 },
              }}
              dangerouslySetInnerHTML={{ __html: cr.notes }}
            />
          </Box>
        )}
      </Box>

      {/* Impacted milestones — each row is expandable to reveal its
          deliverables (description + start/end + sub-activities). */}
      <SectionCard title="Impacted Milestones">
        {Array.isArray(preview.billingMilestones) &&
        preview.billingMilestones.length > 0 ? (
          <ImpactTable
            labelHeader="Group Name"
            defaultOpen
            variant="milestone"
            rows={preview.billingMilestones.map((bm) => ({
              label: bm.groupName,
              amountFrom: bm.originalAmount,
              amountTo: bm.proposedAmount,
              dateFrom: bm.originalDueDate,
              dateTo: bm.proposedDueDate,
              deliverables: parseDeliverables(
                bm.deliverables ?? bm.proposedDeliverables,
              ),
            }))}
          />
        ) : (
          <EmptyHint text="No milestone changes." />
        )}
      </SectionCard>

      {/* Server-side impacted payment lines, if any. */}
      {Array.isArray(preview.paymentLines) &&
        preview.paymentLines.length > 0 && (
          <SectionCard title="Impacted Payments">
            <ImpactTable
              labelHeader="Payment Line"
              rows={preview.paymentLines.map((pl) => ({
                label: pl.displayName,
                amountFrom: pl.originalAmount,
                amountTo: pl.proposedAmount,
                dateFrom: pl.originalDueDate,
                dateTo: pl.proposedDueDate,
              }))}
            />
          </SectionCard>
        )}

      {preview.paymentPlan &&
        Array.isArray(preview.paymentPlan.paymentSchedule) &&
        preview.paymentPlan.paymentSchedule.length > 0 && (
          <PaymentPlanPreview
            strategy={preview.paymentPlan.strategy}
            schedule={preview.paymentPlan.paymentSchedule}
          />
        )}

      {/* Attached files */}
      {Array.isArray(preview.attachedFiles) &&
        preview.attachedFiles.length > 0 && (
          <SectionCard title="Attached Files">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {preview.attachedFiles.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: 13,
                    color: "#0D0D0E",
                    fontFamily: "Inter Tight",
                  }}
                >
                  <span>#{f.id}</span>
                  <a
                    href={f.blobUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#4D6BF6", textDecoration: "none" }}
                  >
                    {f.tag || "View file"}
                  </a>
                </Box>
              ))}
            </Box>
          </SectionCard>
        )}
    </Box>
  );
}

// Stacked +/- delta rendered under the Original Project Cost. Green
// when positive, red when negative, hidden when exactly zero.
const PriceChangeDelta = ({ value }) => {
  const n = Number(value || 0);
  if (Math.abs(n) < 0.0001) return null;
  const positive = n > 0;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.75,
        fontSize: 14,
        fontWeight: 600,
        color: positive ? "#10B981" : "#EF4444",
        fontFamily: "Inter Tight",
      }}
    >
      {positive ? "+" : "-"}
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          color: positive ? "#10B981" : "#EF4444",
        }}
      >
        {renderCurrencyIconColored("black-xs-small")}
      </Box>
      {formattedAmountWithoutAed(Math.abs(n))}
    </Box>
  );
};

const SummaryStat = ({ label, value, highlight }) => (
  <Box>
    <Typography
      sx={{
        fontSize: 12,
        color: "#9CA3AF",
        fontFamily: "Inter Tight",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: highlight ? 18 : 16,
        fontWeight: 600,
        color: highlight ? "#4D6BF6" : "#0D0D0E",
        fontFamily: "Inter Tight",
      }}
    >
      {renderCurrencyIconColored("black-small")}
      {formattedAmountWithoutAed(Number(value || 0))}
    </Box>
  </Box>
);

// Server-rendered payment plan. Pulled from `preview.paymentPlan` so
// it always reflects the saved POST response, not local Payments-tab
// state.
const PaymentPlanPreview = ({ strategy, schedule }) => {
  const upper = String(strategy || "").toUpperCase();
  return (
    <SectionCard title="Payment Plan">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.5fr 1fr 1fr",
            gap: 2,
            fontSize: 12,
            color: "#9CA3AF",
            fontFamily: "Inter Tight",
            fontWeight: 500,
            px: 1,
          }}
        >
          <Box>Payment</Box>
          <Box>Amount</Box>
          <Box>Due Date</Box>
        </Box>

        {(schedule || []).map((l, idx) => (
          <Box
            key={l?.id ?? idx}
            sx={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1fr 1fr",
              gap: 2,
              p: 1.25,
              borderRadius: "8px",
              backgroundColor: "#F9FAFB",
              alignItems: "center",
              fontSize: 13,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            <Box sx={{ fontWeight: 500 }}>
              {l?.displayName ||
                (upper === "ONE_TIME"
                  ? "One-time Payment"
                  : `Payment ${idx + 1}`)}
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              {renderCurrencyIcon("black-xs-small")}
              {formattedAmountWithoutAed(Number(l?.amount || 0))}
            </Box>
            <Box>{formatDate(l?.dueDate)}</Box>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
};

const SectionCard = ({ title, children }) => (
  <Box
    sx={{
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      p: 2.5,
    }}
  >
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: "#0D0D0E",
        fontFamily: "Inter Tight",
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const EmptyHint = ({ text }) => (
  <Typography
    sx={{
      fontSize: 13,
      color: "#9CA3AF",
      fontFamily: "Inter Tight",
      py: 1.5,
      textAlign: "center",
    }}
  >
    {text}
  </Typography>
);

// Stacked impact cell — small grey heading on top, value aligned to
// the start below. Replaces the column-style table layout.
const ImpactCell = ({ heading, children, valueSx }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.5,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        color: "#9CA3AF",
        fontFamily: "Inter Tight",
        fontWeight: 500,
        letterSpacing: 0.2,
      }}
    >
      {heading}
    </Typography>
    <Box
      sx={{
        fontSize: 13,
        color: "#0D0D0E",
        fontFamily: "Inter Tight",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        ...(valueSx || {}),
      }}
    >
      {children}
    </Box>
  </Box>
);

// Whole-day difference between two date-like values. Returns null
// when either side is missing or unparseable. Positive ⇒ new date is
// after old date (the delay direction).
const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const da = new Date(a);
  const db = new Date(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return null;
  const dayMs = 1000 * 60 * 60 * 24;
  return Math.round((db.getTime() - da.getTime()) / dayMs);
};

// Delay indicator chip — red "+N days delayed" when the new due date
// pushes out; yellow "No Change" otherwise. Falls back to a dash when
// either date is unavailable.
const DelayedChip = ({ oldDate, newDate }) => {
  const gap = daysBetween(oldDate, newDate);
  if (gap == null) {
    return (
      <Typography
        sx={{ fontSize: 13, color: "#6B7280", fontFamily: "Inter Tight" }}
      >
        -
      </Typography>
    );
  }
  if (gap > 0) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 8px",
          borderRadius: "999px",
          backgroundColor: "#FEE2E2",
          color: "#B91C1C",
          fontSize: 12,
          fontWeight: 600,
          fontFamily: "Inter Tight",
          whiteSpace: "nowrap",
        }}
      >
        {`+${gap} day${gap === 1 ? "" : "s"} delayed`}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: "999px",
        backgroundColor: "#FEF3C7",
        color: "#92400E",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "Inter Tight",
        whiteSpace: "nowrap",
      }}
    >
      No Change
    </Box>
  );
};

// Small dark numbered badge shown on the right end of every milestone
// row (1, 2, 3 …).
const RowIndexBadge = ({ index }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      borderRadius: "6px",
      backgroundColor: "#0D0D0E",
      color: "#fff",
      fontSize: 11,
      fontWeight: 600,
      fontFamily: "Inter Tight",
      flexShrink: 0,
    }}
  >
    {index}
  </Box>
);

const ImpactRow = ({
  row,
  labelHeader = "Group Name",
  defaultOpen = false,
  variant = "default",
  rowIndex,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const dels = Array.isArray(row.deliverables) ? row.deliverables : [];
  const hasDels = dels.length > 0;

  const newAmount =
    row.amountTo != null && row.amountTo !== ""
      ? Number(row.amountTo)
      : Number(row.amountFrom ?? 0);
  const oldAmount = Number(row.amountFrom ?? 0);
  const newDate =
    row.dateTo != null && row.dateTo !== "" ? row.dateTo : row.dateFrom;
  const oldDate = row.dateFrom;

  const amountChanged = Number(oldAmount) !== Number(newAmount);
  const dateChanged = isoDay(oldDate) !== isoDay(newDate);
  const hasOldAmount = oldAmount > 0;

  if (variant === "milestone") {
    const delayGap = daysBetween(oldDate, newDate);
    const hasDelayed = delayGap != null;
    return (
      <Box
        sx={{
          borderRadius: "10px",
          backgroundColor: "#F9FAFB",
          overflow: "hidden",
        }}
      >
        {/* Mobile layout — 4 stacked rows. */}
        <Box
          onClick={hasDels ? () => setOpen((o) => !o) : undefined}
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 1.25,
            fontFamily: "Inter Tight",
            p: 1.75,
            cursor: hasDels ? "pointer" : "default",
          }}
        >
          {/* Row 1 — index + name on the left, chevron on the right. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
              {rowIndex != null && <RowIndexBadge index={rowIndex} />}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.label || "-"}
              </Typography>
            </Box>
            {hasDels && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 22,
                  color: "#6B7280",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.15s",
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          {/* Row 2 — old / new amount. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            <ImpactCell heading="Old Amount">
              {hasOldAmount ? (
                <>
                  {renderCurrencyIcon("black-xs-small")}
                  {formattedAmountWithoutAed(oldAmount)}
                </>
              ) : (
                "-"
              )}
            </ImpactCell>
            <ImpactCell
              heading="New Amount"
              valueSx={{
                color: amountChanged ? "#4D6BF6" : "#0D0D0E",
                fontWeight: amountChanged ? 600 : 400,
              }}
            >
              {renderCurrencyIconColored("black-xs-small")}
              {formattedAmountWithoutAed(newAmount)}
            </ImpactCell>
          </Box>

          {/* Row 3 — old / new due date. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            <ImpactCell heading="Old Due Date">
              {formatDate(oldDate)}
            </ImpactCell>
            <ImpactCell
              heading="New Due Date"
              valueSx={{
                color: dateChanged ? "#4D6BF6" : "#0D0D0E",
                fontWeight: dateChanged ? 600 : 400,
              }}
            >
              {formatDate(newDate)}
            </ImpactCell>
          </Box>

          {/* Row 4 — delayed chip, only when a value is computable. */}
          {hasDelayed && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <DelayedChip oldDate={oldDate} newDate={newDate} />
            </Box>
          )}
        </Box>

        {/* Desktop / tablet layout — single-row grid. */}
        <Box
          onClick={hasDels ? () => setOpen((o) => !o) : undefined}
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "auto 1.5fr 1fr 1fr 1fr 1fr 1.2fr 32px",
            gap: 2,
            fontFamily: "Inter Tight",
            p: 1.75,
            alignItems: "center",
            cursor: hasDels ? "pointer" : "default",
          }}
        >
          {/* Left rail — numbered badge. */}
          {rowIndex != null ? (
            <RowIndexBadge index={rowIndex} />
          ) : (
            <Box sx={{ width: 22, height: 22 }} />
          )}

          {/* Name only — no "Group Name" heading above. */}
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            {row.label || "-"}
          </Typography>

          <ImpactCell heading="Old Amount">
            {hasOldAmount ? (
              <>
                {renderCurrencyIcon("black-xs-small")}
                {formattedAmountWithoutAed(oldAmount)}
              </>
            ) : (
              "-"
            )}
          </ImpactCell>

          <ImpactCell
            heading="New Amount"
            valueSx={{
              color: amountChanged ? "#4D6BF6" : "#0D0D0E",
              fontWeight: amountChanged ? 600 : 400,
            }}
          >
            {renderCurrencyIconColored("black-xs-small")}
            {formattedAmountWithoutAed(newAmount)}
          </ImpactCell>

          <ImpactCell heading="Old Due Date">{formatDate(oldDate)}</ImpactCell>

          <ImpactCell
            heading="New Due Date"
            valueSx={{
              color: dateChanged ? "#4D6BF6" : "#0D0D0E",
              fontWeight: dateChanged ? 600 : 400,
            }}
          >
            {formatDate(newDate)}
          </ImpactCell>

          {/* Delayed chip — no heading label. */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <DelayedChip oldDate={oldDate} newDate={newDate} />
          </Box>

          {/* Right rail — expand chevron. Down = collapsed, up = open. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {hasDels && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 22,
                  color: "#6B7280",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.15s",
                }}
              />
            )}
          </Box>
        </Box>

        {hasDels && open && (
          <Box
            sx={{
              backgroundColor: "#F9FAFB",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#6B7280",
                  fontFamily: "Inter Tight",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Deliverables
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 22,
                  height: 20,
                  padding: "0 6px",
                  borderRadius: "999px",
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  color: "#6B7280",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "Inter Tight",
                }}
              >
                {dels.length}
              </Box>
            </Box>
            {dels.map((d, di) => (
              <DeliverableCard key={d.id || di} deliverable={d} index={di} />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Default / payments variant — original 5-column layout, unchanged.
  return (
    <Box
      sx={{
        borderRadius: "10px",
        backgroundColor: "#F9FAFB",
        overflow: "hidden",
      }}
    >
      <Box
        onClick={hasDels ? () => setOpen((o) => !o) : undefined}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.5fr 1fr 1fr 1fr 1fr" },
          gap: 2,
          fontFamily: "Inter Tight",
          p: 1.75,
          alignItems: "flex-start",
          cursor: hasDels ? "pointer" : "default",
        }}
      >
        <ImpactCell
          heading={labelHeader}
          valueSx={{ fontWeight: 600, fontSize: 14, gap: 0.5 }}
        >
          {hasDels && (
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "#6B7280",
                transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.15s",
              }}
            />
          )}
          {row.label || "-"}
        </ImpactCell>

        <ImpactCell heading="Old Amount">
          {renderCurrencyIcon("black-xs-small")}
          {formattedAmountWithoutAed(oldAmount)}
        </ImpactCell>

        <ImpactCell
          heading="New Amount"
          valueSx={{
            color: amountChanged ? "#4D6BF6" : "#0D0D0E",
            fontWeight: amountChanged ? 600 : 400,
          }}
        >
          {renderCurrencyIconColored("black-xs-small")}
          {formattedAmountWithoutAed(newAmount)}
        </ImpactCell>

        <ImpactCell heading="Old Due Date">{formatDate(oldDate)}</ImpactCell>

        <ImpactCell
          heading="New Due Date"
          valueSx={{
            color: dateChanged ? "#4D6BF6" : "#0D0D0E",
            fontWeight: dateChanged ? 600 : 400,
          }}
        >
          {formatDate(newDate)}
        </ImpactCell>
      </Box>
    </Box>
  );
};

// Deliverable card used inside the milestone variant — bold name with
// a date-range chip, then dot-bulleted sub-activities below. Honors
// the DELETED status by rendering strikethrough so the audit trail is
// visible without losing context.
const DeliverableCard = ({ deliverable, index }) => {
  const d = deliverable;
  const delDeleted = d.status === "DELETED";
  const days = daysBetween(d.start_date, d.end_date);
  const hasDates = !!(d.start_date && d.end_date);
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "10px",
        p: 2,
        opacity: delDeleted ? 0.6 : 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: delDeleted ? "#6B7280" : "#0D0D0E",
          fontFamily: "Inter Tight",
          textDecoration: delDeleted ? "line-through" : "none",
          mb: 0.75,
        }}
      >
        {d.deliverable_description || d.name || `Deliverable ${index + 1}`}
      </Typography>
      {hasDates && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: "#6B7280",
            fontFamily: "Inter Tight",
            fontSize: 13,
            textDecoration: delDeleted ? "line-through" : "none",
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
          <span>{formatDate(d.start_date)}</span>
          <ArrowForwardIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
          <span>{formatDate(d.end_date)}</span>
          {days != null && days >= 0 && (
            <Box
              sx={{
                ml: 0.5,
                padding: "1px 8px",
                borderRadius: "999px",
                backgroundColor: "#F3F4F6",
                color: "#6B7280",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {`${days} day${days === 1 ? "" : "s"}`}
            </Box>
          )}
        </Box>
      )}
      {Array.isArray(d.sub_activities) && d.sub_activities.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: "1px dashed #E5E7EB",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {d.sub_activities.map((s, si) => {
            const subDeleted = delDeleted || s.status === "DELETED";
            return (
              <Box
                key={s.id || si}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 13,
                  color: subDeleted ? "#9CA3AF" : "#0D0D0E",
                  fontFamily: "Inter Tight",
                  textDecoration: subDeleted ? "line-through" : "none",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: subDeleted ? "#9CA3AF" : "#4D6BF6",
                    flexShrink: 0,
                  }}
                />
                {s.sub_activity_description || s.name || ""}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// No top-level table head — each row carries its own per-cell
// heading + value stack, so the layout reads as a deck of cards.
const ImpactTable = ({
  rows,
  labelHeader = "Group Name",
  defaultOpen = false,
  variant = "default",
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {rows.map((r, idx) => (
      <ImpactRow
        key={idx}
        row={r}
        labelHeader={labelHeader}
        defaultOpen={defaultOpen}
        variant={variant}
        rowIndex={idx + 1}
      />
    ))}
  </Box>
);
